'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Callout, OtpInput, TextButton } from '@galapagos/design-system/web';
import type { OtpChannel } from '@galapagos/shared/api/auth';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { PrivateFields } from '../components/PrivateFields';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupIdentityStyle } from '../components/signup-identity-style';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { persistSignupChannel } from '@/services/session';
import { stepAfterAccount } from '../health-plan/plan-details';
import { useResendCountdown } from '../hooks/useResendCountdown';
import { useSignupApi } from '../hooks/useSignupApi';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupSponsor } from '../hooks/useSignupSponsor';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';
import { phoneDisplay } from '../services/identity-fields';
import { isContactOnAnotherAccount } from '../services/contact-on-another-account';
import { loginDestination, SIGNUP_DOWNLOAD_STEP } from '../services/login-resume';
import { readWithFallback } from '../services/read-diagnostics';
import { sendFailureStatus } from '../services/send-failure-status';
import { signInWithSession } from '../services/sign-in';
import {
  SignupRequestError,
  type SignupOtpInitiate,
  type SignupOtpVerify,
} from '../services/signup-api/signup-api';
import { SIGNUP_LOGIN_PAGE, type SignupPageId } from '../off-path-pages';
import { useSignupFlowStore, type SignupSignInContact } from '../stores/signup-flow-store';
import { EarlierAnswersGate } from './EarlierAnswersGate';
import { ResumedSignInSkip } from './ResumedSignInSkip';

const CODE_LENGTH = 6;
const CODE_INPUT_ID = 'signup-code';

/** Codes after which the member needs a new code, not another try. */
const EXPIRED_CODES = new Set(['otp_expired', 'otp_not_found', 'otp_max_attempts']);

/** Where the code went, as the code screen needs it. */
export interface CodeDelivery {
  /** What the verify and resend requests name. */
  target: SignupOtpInitiate;
  channel: OtpChannel;
  /** The destination as the sent callout shows it. */
  shownAs: string;
  /** False once the flow has forgotten this delivery (a sign-out, a new contact). */
  stillCurrent: () => boolean;
}

/** A code sent to a contact the member typed, which the flow store holds. */
export function contactDelivery(contact: SignupSignInContact): CodeDelivery {
  return {
    target: contact,
    channel: contact.channel,
    shownAs: contact.channel === 'email' ? contact.destination : phoneDisplay(contact.destination),
    stillCurrent: () => useSignupFlowStore.getState().signInContact === contact,
  };
}

interface CodeEntryProps {
  delivery: CodeDelivery;
  /** Sent with the code: a new signup's consents, or nothing for a returning member's sign-in. */
  consent?: SignupOtpVerify['consent'];
  /**
   * Runs once the member is signed in; the code stays in its checking state
   * until it settles. `stillCurrent` turns false once this screen is gone.
   * Skipped for a returning Pelago 1.0 member, who goes to the download page.
   */
  afterSignIn: (stillCurrent: () => boolean) => void | Promise<void>;
  /**
   * Runs instead of `afterSignIn` for a returning Pelago 1.0 member, just
   * before the flow sends them to the download page: whatever this step keeps
   * for a signup still in progress is done with.
   */
  onAlreadyMember?: () => void;
  /** Handles a verify rejection this step treats specially; returns true when it did. */
  onRejected?: (error: unknown) => boolean;
  /** Handles a resend rejection this step treats specially; returns true when it did. */
  onResendRejected?: (error: unknown) => boolean;
}

/**
 * The one-time code screen (v9 1.5 and 4.2): the code input, the sent callout,
 * Send again and Continue. The signup code step, the returning-member login
 * code step and the employer SSO code step all render it, differing only in
 * what they send with the code and what happens after sign-in.
 */
export function CodeEntry({
  delivery,
  consent,
  afterSignIn,
  onAlreadyMember,
  onRejected,
  onResendRejected,
}: CodeEntryProps) {
  const { t } = useTranslation('signup');
  const title = t('code.title');
  const headingRef = useStepHeading(title);
  const api = useSignupApi();
  const { goToStep } = useSignupNavigation();
  const resendHint = useSignupFlowStore((state) => state.otpResendAfterSeconds);
  const setOtpResendAfterSeconds = useSignupFlowStore((state) => state.setOtpResendAfterSeconds);
  const countdown = useResendCountdown(resendHint);

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | undefined>();
  // What the polite region says; a failure also shows as a visible callout.
  const [status, setStatus] = useState<{ text: string; failed: boolean }>({
    text: '',
    failed: false,
  });
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  // Refs guard re-entry: the sixth digit and a Continue press can land in the
  // same tick, before either state update is visible.
  const verifyingRef = useRef(false);
  const resendingRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  // A check that resolves after this screen has gone (a sign-out cleared the
  // contact, or the member left) must not sign anyone in or move the flow on.
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);
  const stillCurrent = () => liveRef.current && delivery.stillCurrent();
  ensureSignupStepStyle();
  ensureSignupIdentityStyle();

  const focusCode = () =>
    formRef.current?.querySelector<HTMLInputElement>(`[data-testid="${CODE_INPUT_ID}"]`)?.focus();
  const clearStatus = () => setStatus({ text: '', failed: false });

  const verify = async (entered: string) => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setVerifying(true);
    clearStatus();
    try {
      const { migrationStartRequired, ...session } = await api.verifyOtp({
        ...delivery.target,
        code: entered,
        ...(consent === undefined ? {} : { consent }),
      });
      if (!stillCurrent()) return;
      signInWithSession(session);
      // Kept with the session, so a reload that lost the contact can tell
      // which of the member's contacts they signed in with.
      persistSignupChannel(delivery.channel === 'email' ? 'email' : 'phone');
      // A Pelago 1.0 member who has not moved across yet already has an
      // account: production sends a returning member who finished signing up
      // to the download page, and the app is where their account moves
      // across. Whichever screen sent the code, signup has nothing left for
      // them, so its next step never runs.
      if (migrationStartRequired) {
        onAlreadyMember?.();
        goToStep(SIGNUP_DOWNLOAD_STEP);
        return;
      }
      await afterSignIn(stillCurrent);
    } catch (error) {
      if (!stillCurrent()) return;
      if (onRejected?.(error)) return;
      if (error instanceof SignupRequestError && error.code === 'invalid_otp') {
        setCodeError(t('code.invalid'));
        setCode('');
      } else if (
        error instanceof SignupRequestError &&
        error.code !== undefined &&
        EXPIRED_CODES.has(error.code)
      ) {
        setCodeError(t('code.expired'));
        setCode('');
      } else {
        setStatus({ text: t('account.genericError'), failed: true });
      }
    } finally {
      verifyingRef.current = false;
      setVerifying(false);
    }
  };

  // Focus returns once the input is enabled again, after a failed check.
  useEffect(() => {
    if (verifying || !codeError) return;
    formRef.current?.querySelector<HTMLInputElement>(`[data-testid="${CODE_INPUT_ID}"]`)?.focus();
  }, [verifying, codeError]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (code.length < CODE_LENGTH) {
      setCodeError(delivery.channel === 'email' ? t('code.requiredEmail') : t('code.required'));
      focusCode();
      return;
    }
    void verify(code);
  };

  const resend = async () => {
    if (resendingRef.current || countdown.seconds > 0) return;
    resendingRef.current = true;
    setResending(true);
    clearStatus();
    try {
      const sent = await api.initiateOtp(delivery.target);
      const hint = sent.resend_available_in_seconds ?? null;
      setOtpResendAfterSeconds(hint);
      countdown.restart(hint);
      setCode('');
      setCodeError(undefined);
      setStatus({ text: t('code.resent'), failed: false });
    } catch (error) {
      if (!stillCurrent()) return;
      if (onResendRejected?.(error)) return;
      setStatus({ text: sendFailureStatus(t, error), failed: true });
    } finally {
      resendingRef.current = false;
      setResending(false);
    }
  };

  const byEmail = delivery.channel === 'email';
  const destination = delivery.shownAs;

  return (
    <SignupShell>
      <form ref={formRef} className="signup-step" noValidate onSubmit={onSubmit}>
        <PageTitle
          title={title}
          description={byEmail ? t('code.descriptionEmail') : t('code.description')}
          headingRef={headingRef}
        />
        {/* analytics-exempt: forwarded-wrapper — the web OtpInput tags its own input (ui.otp-input.change). */}
        <OtpInput
          length={CODE_LENGTH}
          value={code}
          onChange={(next) => {
            setCode(next);
            if (codeError) setCodeError(undefined);
          }}
          onComplete={(entered) => void verify(entered)}
          error={codeError !== undefined}
          errorText={codeError}
          verifying={verifying}
          verifyingText={t('code.verifying')}
          accessibilityLabel={t('code.inputLabel')}
          data-testid={CODE_INPUT_ID}
        />
        {/* Names the member's phone or email, so it stays out of capture. */}
        <PrivateFields>
          <Callout
            tone="success"
            title={t('code.sentTitle', { destination })}
            body={
              countdown.seconds > 0
                ? t('code.waitBody', { count: countdown.seconds })
                : t('code.readyBody')
            }
            data-testid="signup-code-sent"
          />
        </PrivateFields>
        <div className="signup-code-help">
          <p className="signup-code-help__prompt">
            {byEmail ? t('code.didNotGetEmail') : t('code.didNotGet')}
          </p>
          <TextButton
            type="Link"
            size="M"
            label={t('code.sendAgain')}
            state={countdown.seconds > 0 ? 'Disabled' : 'Default'}
            loading={resending}
            onPress={() => void resend()}
            actionId="signup.code-screen-resend.activate"
            data-testid="signup-code-resend"
          />
        </div>
        {/* The one status announcer for this step: always mounted, its text
            changes. The visible callout is hidden from assistive technology so
            a failure is not announced twice. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status">
          {status.text}
        </p>
        {status.failed ? (
          <div aria-hidden="true">
            <Callout tone="error" body={status.text} />
          </div>
        ) : null}
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            htmlType="submit"
            state={verifying ? 'Loading' : 'Default'}
            actionId="signup.code-screen-continue.activate"
            data-testid="signup-continue"
          />
        </FormActions>
      </form>
    </SignupShell>
  );
}

/**
 * Without a contact (a reload clears the in-memory store) there is no code to
 * check, so the step that sends one opens instead.
 */
export function ReturnToStep({ page }: { page: SignupPageId }) {
  const { goToStep } = useSignupNavigation();
  useEffect(() => {
    goToStep(page);
  }, [goToStep, page]);
  return null;
}

/** The consents the account step required, sent with the code that creates the account. */
export const SIGNUP_CONSENT = { termsConsent: true, hipaaConsent: true } as const;

function SignupCodeStep({ contact }: { contact: SignupSignInContact }) {
  const api = useSignupApi();
  const { goToStep, path } = useSignupNavigation();
  const setLoginNotice = useSignupFlowStore((state) => state.setLoginNotice);
  const linkParams = useLegacySignupParams();
  // Cached from the earlier steps; still pending or failed means no plan details.
  const sponsor = useSignupSponsor(linkParams.clientId, linkParams.subClientId);

  // The code signs a member who already finished signing up into their
  // account, and their profile cannot be written again: production sends them
  // to the download page, as the login and SSO code steps do here. A profile
  // that cannot be read leaves signup to carry on.
  const afterSignIn = async (stillCurrent: () => boolean) => {
    const profile = await readWithFallback(
      'registration_profile_after_code',
      'continue_signup',
      () => api.getRegistrationProfile(),
    );
    if (!stillCurrent()) return;
    if (loginDestination(profile) === SIGNUP_DOWNLOAD_STEP) {
      goToStep(SIGNUP_DOWNLOAD_STEP);
      return;
    }
    goToStep(stepAfterAccount({ features: sponsor.data?.features, flow: linkParams.flow, path }));
  };

  // The contact already belongs to another member: no account is made, and
  // the login page tells them to sign in with the contact they joined with.
  const onRejected = (error: unknown) => {
    if (!isContactOnAnotherAccount(error)) return false;
    setLoginNotice('contact-on-another-account');
    goToStep(SIGNUP_LOGIN_PAGE);
    return true;
  };

  return (
    <CodeEntry
      delivery={contactDelivery(contact)}
      consent={SIGNUP_CONSENT}
      afterSignIn={afterSignIn}
      onRejected={onRejected}
    />
  );
}

/** `/code/`: checks the one-time code the account step sent by text or email (v9 1.5). */
export function CodeStepScreen() {
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  const contact = useSignupFlowStore((state) => state.signInContact);
  return (
    <EarlierAnswersGate step="code">
      <ResumedSignInSkip>
        {contact ? <SignupCodeStep contact={contact} /> : <ReturnToStep page="account" />}
      </ResumedSignInSkip>
    </EarlierAnswersGate>
  );
}
